// ABOUTME: Modal displayed when user hits usage limit (messages, API calls, etc.).
// ABOUTME: Shows usage count, reset time, and upgrade button.

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, X } from 'lucide-react';
import { Button } from '../ui/Button';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: string;
  used: number;
  limit: number;
  onUpgrade: () => void;
  resetsAt?: Date;
  error?: string | null;
}

export function UpgradeModal({
  isOpen,
  onClose,
  resource,
  used,
  limit,
  onUpgrade,
  resetsAt,
  error,
}: UpgradeModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Calculate days until reset
  const getDaysUntilReset = () => {
    if (!resetsAt) return null;
    const now = new Date();
    const diff = resetsAt.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysUntilReset = getDaysUntilReset();

  // Format resource name for title
  const resourceLabel = resource.charAt(0).toUpperCase() + resource.slice(1);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"
          >
            <div className="card rounded-sm overflow-hidden shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-primary-light">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <h2 id="upgrade-modal-title" className="text-lg font-medium text-surface-900">
                    Monthly {resourceLabel} Limit Reached
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="p-2 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5 text-surface-400" />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <div className="mb-4">
                  <p className="text-sm text-surface-600 leading-relaxed mb-2">
                    You've used{' '}
                    <span className="font-semibold text-surface-900">
                      {used} / {limit} {resource}
                    </span>{' '}
                    this month.
                  </p>
                  {daysUntilReset !== null && (
                    <p className="text-sm text-surface-500">
                      Resets in {daysUntilReset} {daysUntilReset === 1 ? 'day' : 'days'}
                    </p>
                  )}
                </div>
                <p className="text-sm text-surface-600 leading-relaxed">
                  Upgrade to continue using {resource} without interruption and unlock higher limits.
                </p>
                {error && (
                  <p className="mt-3 text-sm text-red-600" role="alert">
                    {error}
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-surface-200 bg-surface-50">
                <Button
                  variant="ghost"
                  onClick={onClose}
                >
                  Maybe Later
                </Button>
                <Button
                  variant="primary"
                  onClick={onUpgrade}
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
